import React, { useState } from 'react';
import { ClipboardIcon, CheckIcon } from '@heroicons/react/24/outline';
import { CouponReturn } from '../clients/rust-api-client';
import { memberBillingService } from '../clients/ApiClients';
import { Button } from '../Components/Catalyst/button';
import { Subheading } from '../Components/Catalyst/heading';
import { Text } from '../Components/Catalyst/text';
import {
  billingErrorMessage,
  formatBillingDate,
  formatMoney,
  savePdfResponse,
} from '../utils/billingHelpers';

interface Props {
  homeId: string;
  coupon: CouponReturn | null;
  onError: (message: string) => void;
}

const Row: React.FC<{
  label: string;
  value: React.ReactNode;
  strong?: boolean;
}> = ({ label, value, strong }) => (
  <div className="flex items-baseline justify-between gap-4 border-b border-zinc-100 py-3 last:border-0 dark:border-zinc-700/60">
    <span className="text-sm text-zinc-500 dark:text-zinc-400">{label}</span>
    <span
      className={
        strong
          ? 'text-right text-base font-semibold text-zinc-900 dark:text-white'
          : 'text-right text-sm text-zinc-900 dark:text-white'
      }
    >
      {value}
    </span>
  </div>
);

/**
 * How to pay — the portal's primary action.
 *
 * There is no "Pay now" button because there is no member payment
 * endpoint: everything that moves money is a management action. That is
 * not purely a gap. Paying the association directly
 * [avoids the convenience fee](../../../knowledge/notes/hoa-payments-domain.md)
 * the software path would charge, so the account number and the
 * payable-to line genuinely are what most homeowners want.
 *
 * **The scanline is not shown.** front-porch prints it, correctly — a
 * manager may need to check what a lockbox will read, and that screen
 * carries the caveat that its format is inferred from a single bank
 * sample with the check digit omitted. A homeowner has no use for it
 * and no way to act on the caveat, so it stays on the coupon PDF.
 */
const PayCard: React.FC<Props> = ({ homeId, coupon, onError }) => {
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyAccountNumber = async (): Promise<void> => {
    if (!coupon) return;
    try {
      await navigator.clipboard.writeText(coupon.account_number);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be refused; the number is on screen either
      // way, so this is not worth an error banner.
    }
  };

  const download = async (): Promise<void> => {
    try {
      setDownloading(true);
      const response = await memberBillingService.myCouponPdf(
        { homeId },
        { responseType: 'blob' as const }
      );
      savePdfResponse(response, `payment-coupon.pdf`);
    } catch (err) {
      onError(billingErrorMessage(err, "Couldn't download your coupon."));
    } finally {
      setDownloading(false);
    }
  };

  if (!coupon) return null;

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-6 dark:border-zinc-700 dark:bg-gray-800">
      <Subheading>How to pay</Subheading>
      <Text className="mt-1 text-sm">
        Send a check to your association with the coupon below, or use these
        details with your bank&rsquo;s bill pay.
      </Text>

      <div className="mt-4">
        <Row label="Make check payable to" value={coupon.payable_to} />
        <Row
          label="Your account number"
          value={
            <button
              type="button"
              onClick={copyAccountNumber}
              className="inline-flex items-center gap-1.5 font-mono text-sm text-sky-700 hover:underline dark:text-sky-400"
              aria-label="Copy account number"
            >
              {coupon.account_number}
              {copied ? (
                <CheckIcon className="h-4 w-4" />
              ) : (
                <ClipboardIcon className="h-4 w-4" />
              )}
            </button>
          }
        />
        {coupon.current_period_amount && (
          <Row
            label="This period"
            value={formatMoney(coupon.current_period_amount)}
          />
        )}
        <Row label="Amount due" value={formatMoney(coupon.amount_due)} strong />
        {coupon.due_date && (
          <Row label="Due" value={formatBillingDate(coupon.due_date)} />
        )}
        {coupon.grace_date && (
          <Row
            label="Late after"
            value={formatBillingDate(coupon.grace_date)}
          />
        )}
      </div>

      <Button
        color="sky"
        className="mt-5 w-full sm:w-auto"
        onClick={download}
        disabled={downloading}
      >
        {downloading ? 'Preparing…' : 'Download payment coupon (PDF)'}
      </Button>
    </div>
  );
};

export default PayCard;
